import React from "react";
import "./index.css";
import { Formik, Form } from "formik";
import {
  Box,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import GenericTextField from "../generic-components/TextField/TextField";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import DomainVerificationIcon from "@mui/icons-material/DomainVerification";
import CustomButton from "../generic-components/Button/Button";
import PhoneInputField from "../generic-components/PhoneInputField/PhoneInputField";
import { formValidationSchema } from "../../utils/validationSchema";
// import emailjs from "emailjs-com";

function ContactForm() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const initialValues = {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    message: "",
  };

  const handleSubmit = async (values, { resetForm, setSubmitting }) => {
    const scriptURL = "https://script.google.com/macros/s/AKfycbzDG9rvzXEjqaPEGgH2sHyTi4pAtP2nSErq4Vb85AeIJj9OAH1xm15bkdV-JCY5JwDb4Q/exec";
    
    const formData = new FormData();
    formData.append("First Name", values.firstName);
    formData.append("Last Name", values.lastName);
    formData.append("Email", values.email);
    formData.append("Phone", values.phone);
    formData.append("Message", values.message);

    try {
      const response = await fetch(scriptURL, {
        method: "POST",
        body: formData,
      });

      const result = await response.json();
      console.log("Response:", result);

      if (!response.ok) {
        throw new Error(result.error || "Unknown error");
      }

      // await emailjs.send("service_94q8475", "template_d82gzju", values, "gQZ0UvulTbk-FwdS4");
      alert("Thank you for contacting us. We will get back to you shortly");
      resetForm();
    } catch (error) {
      console.error("Error submitting form:", error);
      alert(`Submission failed: ${error.message}`);
    } finally {
      setSubmitting(false); // ✅ stop loader
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={formValidationSchema}
      onSubmit={handleSubmit}
    >
      {({
        handleChange,
        handleBlur,
        values,
        errors,
        touched,
        isSubmitting, // ✅ Formik loader flag
      }) => (
        <Form>
          <Box sx={{ minHeight: "100vh", display: "flex" }}>
            <Stack direction={{ xs: "column", md: "row" }} sx={{ flex: 1 }}>
              {/* Image Section */}
              <Box
                sx={{
                  width: "100%",
                  height: { xs: "50vh", md: "100vh" },
                  flex: 1,
                }}
              >
                <Box
                  component="img"
                  src={
                    isMobile
                      ? "/assets/contact-us-mobile.webp"
                      : "/assets/contact.jpg"
                  }
                  alt="Contact"
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: isMobile ? "cover" : "contain",
                  }}
                />
              </Box>

              {/* Form Section */}
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  p: { xs: 2, md: 4 },
                }}
              >
                <Box sx={{ width: "100%", maxWidth: 800 }}>
                  <Typography variant="h4" mb={1} className="contact-form-title">
                    Let's Get In Touch
                  </Typography>

                  <Typography
                    variant="body1"
                    mb={3}
                    className="contact-form-subtitle"
                  >
                    Or just reach out manually to{" "}
                    <Box component="span" sx={{ color: "#23A215" }}>
                      info@aspiretaxbook.com.au
                    </Box>
                  </Typography>

                  {/* Name Fields */}
                  <Stack
                    spacing={2}
                    direction={{ xs: "column", sm: "row" }}
                    mb={3}
                  >
                    <GenericTextField
                      name="firstName"
                      label="First Name"
                      icon={<PersonIcon />}
                      placeholder="Enter your First Name"
                      value={values.firstName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.firstName && Boolean(errors.firstName)}
                      helperText={touched.firstName && errors.firstName}
                    />

                    <GenericTextField
                      name="lastName"
                      label="Last Name"
                      icon={<PersonIcon />}
                      placeholder="Enter your Last Name"
                      value={values.lastName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.lastName && Boolean(errors.lastName)}
                      helperText={touched.lastName && errors.lastName}
                    />
                  </Stack>

                  {/* Other Fields */}
                  <Stack spacing={3} mb={3}>
                    <GenericTextField
                      name="email"
                      label="Email Address"
                      icon={<EmailIcon />}
                      placeholder="Enter your Email Address"
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && errors.email}
                    />

                    <PhoneInputField
                      name="phone"
                      value={values.phone}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.phone && Boolean(errors.phone)}
                      helperText={touched.phone && errors.phone}
                    />

                    <GenericTextField
                      name="message"
                      label="Message"
                      multiline
                      rows={4}
                      placeholder="Enter your Message here..."
                      value={values.message}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.message && Boolean(errors.message)}
                      helperText={touched.message && errors.message}
                    />
                  </Stack>

                  {/* Submit Button with Loader */}
                  <CustomButton
                    type="submit"
                    disabled={isSubmitting}
                    className="submit-btn"
                    sx={{ width: { xs: "100% !important", sm: "auto" } }}
                    text={
                      isSubmitting ? (
                        <CircularProgress size={22} sx={{ color: "#fff" }} />
                      ) : (
                        "Submit"
                      )
                    }
                    endIcon={
                      !isSubmitting && (
                        <DomainVerificationIcon className="submit-btn-icon" />
                      )
                    }
                  />
                </Box>
              </Box>
            </Stack>
          </Box>
        </Form>
      )}
    </Formik>
  );
}

export default ContactForm;
